import { Field } from '../fields/Field';
import { Field as FieldType } from '../fieldtypes/Field';
import { Message as MessageType } from '../fieldtypes/Message';
import type { IFIXParser } from '../IFIXParser';
import type { Message } from '../message/Message';

/**
 * Handles message sequence validation in a FIX session.
 * This function checks if the received message's sequence number matches the expected sequence number.
 * If there is a mismatch, it sends a ResendRequest message to request the missing messages.
 *
 * @param {IFIXParser} parser - The FIX parser instance handling the session
 * @param {Message} message - The message to validate sequence number for
 * @returns {boolean} - Returns true if the sequence number is valid, false otherwise
 */
export const handleSequence = (parser: IFIXParser, message: Message): boolean => {
    if (
        message.messageSequence !== parser.messageStoreIn.getNextMsgSeqNum() &&
        message.messageType !== MessageType.Logon &&
        message.messageType !== MessageType.SequenceReset
    ) {
        parser.logger.logWarning({
            message: `FIXServer (${parser.protocol?.toUpperCase()}): Expected MsgSeqNum ${parser.messageStoreIn.getNextMsgSeqNum()}, but got ${
                message.messageSequence
            }`,
            fix: message.encode('|'),
        });

        // Message has wrong sequence, respond with ResendRequest
        const resendRequest: Message = parser.createMessage(
            new Field(FieldType.MsgType, MessageType.ResendRequest),
            new Field(FieldType.MsgSeqNum, parser.getNextTargetMsgSeqNum()),
            new Field(FieldType.SenderCompID, parser.sender),
            new Field(FieldType.SendingTime, parser.getTimestamp(new Date())),
            new Field(FieldType.TargetCompID, parser.target),
            new Field(FieldType.BeginSeqNo, parser.messageStoreIn.getNextMsgSeqNum()),
            new Field(FieldType.EndSeqNo, 0),
        );
        parser.logger.log({
            level: 'info',
            message: `FIXServer (${parser.protocol?.toUpperCase()}): Sending ResendRequest from ${parser.messageStoreIn.getNextMsgSeqNum()} to 0`,
            fix: resendRequest.encode('|'),
        });
        parser.send(resendRequest);
        return false;
    }
    return true;
};
